import React, { useEffect, useState } from 'react'
import {
  Drawer,
  DrawerContent,
  DrawerDescription,
  DrawerHeader,
  DrawerTitle,
} from '@/components/ui/drawer'
import { Button } from '@/components/ui/button'
import { ImageUploader } from '@/components/ui/image-uploader'
import { Separator } from '@/components/ui/separator'
import { Cat, CatGender } from '@/type/cat'
import {
  AlignLeft,
  Calendar,
  Loader2,
  MapPin,
  MessageCircleHeart,
  Palette,
  User,
  X,
} from 'lucide-react'
import { useForm } from 'react-hook-form'
import { z } from 'zod'
import { zodResolver } from '@hookform/resolvers/zod'
import LocationPicker from '@/components/modals/LocationPicker'
import { requester } from '@/services/api'
import { toast } from 'react-hot-toast'
import { useCatById } from '@/services/cats/hook'
import { catService } from '@/services/cats/request'

interface CatProfileForm {
  name: string
  gender: CatGender
  color: string
  date_founded: string
  bio: string
  images: string[]
  location: {
    address: string
    lat: number
    lng: number
  }
}

const catProfileSchema = z.object({
  name: z
    .string({
      required_error: 'Name is required',
      invalid_type_error: 'Name must be a string',
    })
    .min(2, { message: 'Name must be at least 2 characters long' })
    .max(10, { message: 'Name must be at most 10 characters long' }),
  gender: z.nativeEnum(CatGender, { required_error: 'Gender is required' }),
  color: z
    .string({ required_error: 'Color is required' })
    .min(1, { message: 'Color must be at least 1 character' }),
  date_founded: z.string().refine((val) => !isNaN(Date.parse(val)), {
    message: 'Invalid date format',
  }),
  bio: z.string(),
  // .min(1, { message: 'Bio must be at least 1 character' }),
  location: z.object({
    address: z
      .string({ required_error: 'Address is required' })
      .min(1, { message: 'Address must be at least 1 character' }),
    lat: z.number(),
    lng: z.number(),
  }),
  images: z.array(z.string()),
})

const EditCatProfile = (props: {
  selectedCat: Cat | undefined
  show: [boolean, (state: boolean) => void]
  onRequestSuccess: () => void
}) => {
  const { selectedCat, show, onRequestSuccess } = props
  const [showModal, setShowModal] = show
  const [imagePreview, setImagePreview] = useState<{
    file: File
    preview: string
  } | null>(null)
  const [locationPickerOpen, setLocationPickerOpen] = useState<boolean>(false)

  const { data: cat } = useCatById(show && selectedCat?.id ? selectedCat?.id : null) // make sure this dialog is open and cat id is not null

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors, isSubmitting },
  } = useForm<CatProfileForm>({
    defaultValues: {
      name: '',
      gender: CatGender.MALE,
      color: '',
      date_founded: '',
      bio: '',
      images: [],
      location: {
        address: '',
        lat: 0,
        lng: 0,
      },
    },
    resolver: zodResolver(catProfileSchema),
  })

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    if (
      file.type !== 'image/jpeg' &&
      file.type !== 'image/png' &&
      file.type !== 'image/jpg'
    ) {
      toast.error('Invalid file type')
      return
    }
    const reader = new FileReader()
    reader.onloadend = () => {
      setImagePreview({
        file: file,
        preview: reader.result as string,
      })
    }
    reader.readAsDataURL(file)
  }

  const handleSaveLocation = (place: {
    place: google.maps.places.Place
    address: string
    lat: number
    lng: number
  }) => {
    setValue('location', {
      address: place.address,
      lat: place.lat,
      lng: place.lng,
    })
    setLocationPickerOpen(false)
  }

  const handleFormSave = async (data: CatProfileForm) => {
    let fileUrl = ''
    try {
      if (!!imagePreview?.file.size) {
        const upload: any = await requester.post('/uploads', {
          fileName: imagePreview?.file.name,
          contentType: imagePreview?.file.type,
          metadata: {},
        })

        const { url, fields } = upload

        if (!url || !fields) {
          throw new Error('Upload file error: Invalid presigned URL response')
        }
        await fetch(url, { method: 'PUT', body: imagePreview?.file })
        fileUrl = `${import.meta.env.VITE_CLOUDFLARE_BASE_URL}/${fields.key}`
      }

      switch (!!cat?.id) {
        case true:
          await catService.putCats(cat?.id as string, {
            ...data,
            images: !!imagePreview?.file.size ? [fileUrl] : cat.images,
          })
          toast.success('Cat profile updated successfully')
          onRequestSuccess()
          break
        case false:
          await catService.postCats({ ...data, images: [fileUrl] })
          toast.success('Cat profile created successfully')
          onRequestSuccess()
          break
      }
    } catch (error) {
      console.log('error', error)
      toast.error('Error saving cat profile')
    }
  }

  useEffect(() => {
    if (!cat) return
    setValue('name', cat?.name)
    setValue('gender', cat?.gender)
    setValue('color', cat?.color)
    setValue('date_founded', cat?.date_founded)
    setValue('bio', cat?.bio)
    setValue('location', cat?.location)
    setImagePreview({
      file: cat?.images[0],
      preview: cat?.images[0],
    })
  }, [cat])

  useEffect(() => {
    if (!selectedCat) return
    setValue('name', selectedCat?.name)
    setValue('gender', selectedCat?.gender)
    setValue('color', selectedCat?.color)
    setValue('date_founded', selectedCat?.date_founded)
    setValue('bio', selectedCat?.bio)
    setValue('location', selectedCat?.location)
    // setImagePreview({
    //   file: selectedCat?.images[0],
    //   preview: selectedCat?.images[0],
    // })
  }, [selectedCat])

  return (
    <Drawer
      open={showModal}
      onOpenChange={(open) => {
        if (!open) {
          setShowModal(false)
        }
      }}
    >
      <DrawerContent className='h-[100vh]'>
        <DrawerHeader>
          <DrawerTitle className='text-2xl font-bold text-center'>
            Cat Profile
          </DrawerTitle>
        </DrawerHeader>
        <DrawerDescription asChild>
          <div className='flex flex-col gap-4 p-4'>
            <X
              className='absolute top-10 left-4 rounded-full border border-black'
              onClick={() => setShowModal(false)}
            />
            <ImageUploader
              imagePreview={imagePreview?.preview as string}
              onImageUpload={handleImageUpload}
            />
            <p
              className='text-center font-medium text-orange-500'
              onClick={() => document.getElementById('image-upload')?.click()}
            >
              Edit Profile
            </p>
            <form
              onSubmit={handleSubmit(handleFormSave)}
              className='flex flex-col gap-4'
            >
              {/* starts:: general info */}
              <div className='flex flex-col gap-3 border border-solid border-gray-300 rounded-lg p-4'>
                <div className='flex justify-between items-center'>
                  <div className='w-1/2 flex items-center gap-2'>
                    <User className='w-4 h-4 text-gray-500' />
                    <p className='text-sm text-gray-500'>Display Name</p>
                    <p className='text-red-500'>*</p>
                  </div>
                  <input
                    type='text'
                    {...register('name')}
                    placeholder="cat's name"
                    className='w-1/2 text-end focus:outline-none'
                  />
                </div>
                <Separator className='h-[1px] bg-gray-300' />
                <div className='flex justify-between items-center'>
                  <div className='w-1/2 flex items-center gap-2'>
                    <MessageCircleHeart className='w-4 h-4 text-gray-500' />
                    <p className='text-sm text-gray-500'>Gender</p>
                  </div>
                  <select
                    {...register('gender')}
                    className='w-1/2 text-end focus:outline-none'
                  >
                    <option value={CatGender.MALE}>Male</option>
                    <option value={CatGender.FEMALE}>Female</option>
                  </select>
                </div>
                <Separator className='h-[1px] bg-gray-300' />
                <div className='flex justify-between items-center'>
                  <div className='w-1/2 flex items-center gap-2'>
                    <Palette className='w-4 h-4 text-gray-500' />
                    <p className='text-sm text-gray-500'>Color</p>
                    <p className='text-red-500'>*</p>
                  </div>
                  <input
                    type='text'
                    {...register('color')}
                    placeholder='Color'
                    className='w-1/2 text-end focus:outline-none'
                  />
                </div>
                <Separator className='h-[1px] bg-gray-300' />
                <div className='flex justify-between items-center'>
                  <div className='w-1/2 flex items-center gap-2'>
                    <Calendar className='w-4 h-4 text-gray-500' />
                    <p className='text-sm text-gray-500'>Date founded</p>
                    <p className='text-red-500'>*</p>
                  </div>
                  <input
                    type='date'
                    {...register('date_founded')}
                    className='text-end focus:outline-none'
                  />
                </div>
              </div>
              {errors.name && (
                <p className='text-red-500 text-sm'>{errors.name.message}</p>
              )}
              {errors.gender && (
                <p className='text-red-500 text-sm'>{errors.gender.message}</p>
              )}
              {errors.color && (
                <p className='text-red-500 text-sm'>{errors.color.message}</p>
              )}
              {errors.date_founded && (
                <p className='text-red-500 text-sm'>
                  {errors.date_founded.message}
                </p>
              )}

              {/* ends:: general info */}
              {/* starts:: cat's bio */}
              <div className='flex flex-col gap-3 border border-solid border-gray-300 rounded-lg p-4'>
                <div className='flex flex-col'>
                  <div className='flex items-center gap-2'>
                    <AlignLeft className='w-4 h-4 text-gray-500' />
                    <p className='text-sm text-gray-500'>Bio</p>
                  </div>
                  <textarea
                    {...register('bio')}
                    placeholder="cat's bio"
                    className='focus:outline-none'
                  />
                  {errors.bio && (
                    <p className='text-red-500 text-sm'>{errors.bio.message}</p>
                  )}
                </div>
              </div>
              {/* ends:: cat's bio */}
              {/* starts:: cat's location */}
              <div
                className='flex flex-col gap-3 border border-solid border-gray-300 rounded-lg p-4 cursor-pointer'
                onClick={() => setLocationPickerOpen(true)}
              >
                <div
                  className='flex flex-col'
                  // onClick={() => setLocationPickerOpen(true)}
                >
                  <div className='flex items-center gap-2'>
                    <MapPin className='w-4 h-4 text-gray-500' />
                    <p className='text-sm text-gray-500'>Location</p>
                  </div>
                  <p className='text-sm text-black'>
                    {watch('location')
                      ? watch('location').address
                      : 'select the location'}
                  </p>
                </div>
                {errors.location && (
                  <p className='text-red-500 text-sm'>
                    {errors.location.message}
                  </p>
                )}
              </div>
              {/* ends:: cat's location */}

              {/* starts:: main action buttons */}
              <div className='w-full'>
                <Button
                  type='submit'
                  variant='primary'
                  size='xl'
                  className='w-full rounded-full px-2 py-6 border-2 border-white drop-shadow-lg text-md'
                >
                  {isSubmitting ? (
                    <Loader2 className='w-4 h-4 animate-spin' />
                  ) : (
                    'Save'
                  )}
                </Button>
              </div>
              {/* ends:: main action buttons */}
            </form>

            <LocationPicker
              location={watch('location')}
              show={[locationPickerOpen, setLocationPickerOpen]}
              onSave={handleSaveLocation}
            />
          </div>
        </DrawerDescription>
      </DrawerContent>
    </Drawer>
  )
}

export default EditCatProfile
